import React, { useState, useEffect, useMemo } from 'react';
import { ChartRenderer } from './charts';
import { normalizeReportChartForDisplay } from './charts/transformWidgetChart';
import {
  MatrixReportTable,
  isMatrixMode,
  buildSummarizedDataFromMatrix,
  collectSummarizedMeasureFields,
  DateGroupGranularityBar,
  matrixConfigHasDateGroups,
  buildDateGroupOptions,
  getReportGroupBucket,
  isDateGroupField,
} from './matrix';
import { sortReportGroupKeys } from './charts/chartPicklistColors';
import { useReportGroupPicklistValues } from './matrix/useReportGroupPicklistValues';
import ColumnHeaderDropdown from './ColumnHeaderDropdown';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatCalculatedValue,
  getCalculatedFormatOptions,
  resolveTokenCurrency,
} from '../../utils/formatUtils';
import { getMaskedReportCellDisplay } from '../../utils/fieldConfigUtils';
import {
  calculateReportAggregation,
  formatAggregatedDisplayForReport,
  formatReportCurrencyCell,
  formatReportCalculatedCell,
  getAggregationNumericResult,
  reportFieldIsPercentLike,
  getReportEmptyDisplay,
  getReportRowFieldSortComparable,
  compareReportSortValues,
  getReportGrandTotalCellValue,
  findTabularGrandTotalLabelIndex,
  isRightAlignedReportField,
} from '../../utils/reportCurrencyAggregate';
import jwt_decode from 'jwt-decode';
import { useReportCurrency } from './hooks/useReportCurrency';
import { logReportCurrency, logReportCurrencyTable } from './debug/reportCurrencyDebug';
import { reportFieldIsCurrencyLike } from '../../utils/reportCurrencyAggregate';
import {
  buildDisplayFieldsWithFormulas,
  formatFormulaDisplayValue,
  getFormulaValueForGroup,
} from '../../utils/formulaFields';

// Resolve currency with priority: token currency (user currency or company currency) > default
const resolveCurrency = (fallbackCurrency = 'INR') => {
  // Check currency from JWT token
  // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
  // So it will be user.currency if available, otherwise company.currency
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwt_decode(token);
      // Currency in token is: user.currency if available, otherwise company.currency
      const tokenCurrency = decoded.currency;
      if (tokenCurrency) {
        return tokenCurrency;
      }
    }
  } catch (error) {
    console.error('Error decoding token for currency:', error);
  }

  // Fallback to default
  return fallbackCurrency;
};

const parseDisplayConfig = (displayConfig) => {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch (e) {
      return {};
    }
  }
  return displayConfig || {};
};

const formatCalculatedReportValue = (fieldData, displayConfig, row = null, field = null) => {
  const value =
    typeof fieldData === 'object' && fieldData !== null
      ? fieldData.value
      : fieldData;
  if (value === null || value === undefined || value === '') {
    return getReportEmptyDisplay(field, {
      displayConfig,
      row,
      fallbackCurrencyCode: resolveTokenCurrency('INR'),
    });
  }
  const formatted = formatCalculatedValue(
    value,
    displayConfig,
    getCalculatedFormatOptions(displayConfig, row, {
      objectName: field?.object_name,
      fallbackCurrencyCode: resolveTokenCurrency('INR'),
    })
  );
  if (formatted === '—') {
    return getReportEmptyDisplay(field, {
      displayConfig,
      row,
      fallbackCurrencyCode: resolveTokenCurrency('INR'),
    });
  }
  return formatted;
};

const reportEmptyCellDisplay = (field, dataType, displayConfig, row) =>
  getReportEmptyDisplay(field, {
    dataType,
    displayConfig,
    row,
    fallbackCurrencyCode: resolveTokenCurrency('INR'),
  });

// Same rules as ReportDataViewer / DetailLayout: universal date & datetime display + user timezone
const formatValue = (fieldData, dataType, displayConfig = null, row = null, field = null) => {
  const dt = String(dataType || '').toLowerCase();
  const maskedDisplay = getMaskedReportCellDisplay(field, fieldData);
  if (maskedDisplay !== null) {
    if (maskedDisplay === '') {
      return reportEmptyCellDisplay(field, dataType, displayConfig, row);
    }
    return maskedDisplay;
  }

  if (dt === 'calculated') {
    return formatCalculatedReportValue(fieldData, displayConfig, row, field);
  }

  // Handle enriched data structure (from backend with display values)
  if (typeof fieldData === 'object' && fieldData !== null) {
    // If it's a lookup field with display value, show the display value
    if (fieldData.isReference && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    // If it's a picklist field with display value, show the display value
    if ((fieldData.isPicklist || fieldData.isMultiPicklist) && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    // Otherwise, use the value property
    const value = fieldData.value;
    if (value === null || value === undefined || value === '') {
      return reportEmptyCellDisplay(field, dataType, displayConfig, row);
    }

    if (dataType === 'rollupsummary' && displayConfig) {
      const parsedConfig = parseDisplayConfig(displayConfig);
      const rollupConfig = parsedConfig.rollupSummary || {};
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();

      if (outputType === 'currency') {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return reportEmptyCellDisplay(field, dataType, displayConfig, row);
        }
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(numValue, currencyCode);
      }
      if (outputType === 'percent') {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return reportEmptyCellDisplay(field, dataType, displayConfig, row);
        }
        return formatPercent(numValue);
      }
      if (outputType === 'date') {
        return formatDate(value);
      }
      if (outputType === 'datetime') {
        return formatDateTime(value);
      }
      const numVal = parseFloat(value);
      if (isNaN(numVal)) {
        return reportEmptyCellDisplay(field, dataType, displayConfig, row);
      }
      return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    switch (dataType) {
      case 'currency': {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return reportEmptyCellDisplay(field, dataType, displayConfig, row);
        }
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(numValue, currencyCode, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      case 'number': {
        const numVal = parseFloat(value);
        if (isNaN(numVal)) {
          return reportEmptyCellDisplay(field, dataType, displayConfig, row);
        }
        return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      case 'percent': {
        const percentVal = parseFloat(value);
        if (isNaN(percentVal)) {
          return reportEmptyCellDisplay(field, dataType, displayConfig, row);
        }
        return formatPercent(percentVal);
      }
      case 'date':
        return formatDate(value);
      case 'datetime':
        return formatDateTime(value);
      default:
        return value.toString();
    }
  }

  // Handle simple value (fallback for backward compatibility)
  const value = fieldData;
  if (value === null || value === undefined || value === '') {
    return reportEmptyCellDisplay(field, dataType, displayConfig, row);
  }

  if (dataType === 'rollupsummary' && displayConfig) {
    const parsedConfig = parseDisplayConfig(displayConfig);
    const rollupConfig = parsedConfig.rollupSummary || {};
    const outputType = (rollupConfig.outputType || 'number').toLowerCase();

    if (outputType === 'currency') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return reportEmptyCellDisplay(field, dataType, displayConfig, row);
      }
      return formatCurrency(numValue, resolveCurrency('INR'));
    }
    if (outputType === 'percent') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return reportEmptyCellDisplay(field, dataType, displayConfig, row);
      }
      return formatPercent(numValue);
    }
    if (outputType === 'date') {
      return formatDate(value);
    }
    if (outputType === 'datetime') {
      return formatDateTime(value);
    }
    const numVal = parseFloat(value);
    if (isNaN(numVal)) {
      return reportEmptyCellDisplay(field, dataType, displayConfig, row);
    }
    return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  switch (dataType) {
    case 'currency': {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return reportEmptyCellDisplay(field, dataType, displayConfig, row);
      }
      return formatCurrency(numValue, resolveCurrency('INR'));
    }
    case 'number':
    case 'rollupsummary': {
      const numVal = parseFloat(value);
      if (isNaN(numVal)) {
        return reportEmptyCellDisplay(field, dataType, displayConfig, row);
      }
      return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'percent': {
      const percentVal = parseFloat(value);
      if (isNaN(percentVal)) {
        return reportEmptyCellDisplay(field, dataType, displayConfig, row);
      }
      return formatPercent(percentVal);
    }
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    default:
      return value.toString();
  }
};

function makePreviewAggregationFormat(baseCurrencyCode) {
  return (result, field, aggregation) =>
    formatAggregatedDisplayForReport(
      result,
      field,
      aggregation,
      baseCurrencyCode,
      (res, fld) =>
        formatValue(res, fld.data_type, fld.display_config || fld.displayConfig, null, fld)
    );
}

// Function to render grouped data
const renderGroupedData = (
  data,
  selectedFields,
  grouping,
  summarizedFields,
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  reportSortKeys = [],
  formulaFields = [],
  formulaSummarizedData = null,
  matrixDateGranularity = 'month',
  dateGranularityByFieldId = {},
  reportFieldsForSort = [],
  picklistValuesByFieldId = null,
  primaryObjectName = null
) => {
  const aggregationFormatter = makePreviewAggregationFormat(baseCurrencyCode);
  const dateGroupOptions = buildDateGroupOptions(
    matrixDateGranularity,
    dateGranularityByFieldId
  );

  const formatDataCell = (fieldData, field, row) => {
    const money = formatReportCurrencyCell(
      fieldData,
      field,
      row,
      baseCurrencyCode,
      resolveCurrency('INR'),
      exchangeRateLookup
    );
    if (money != null) return money;
    const calculated = formatReportCalculatedCell(
      fieldData,
      field,
      row,
      baseCurrencyCode || resolveTokenCurrency('INR')
    );
    if (calculated != null) return calculated;
    return formatValue(
      fieldData,
      field.data_type,
      field.display_config || field.displayConfig,
      row,
      field
    );
  };

  // Get all grouped fields
  const groupedFields = grouping.groupedFields;

  console.log('renderGroupedData called with:', {
    dataLength: data.length,
    selectedFieldsLength: selectedFields.length,
    grouping,
    summarizedFields,
    sampleData: data.length > 0 ? data[0] : 'No data',
    groupedFields: groupedFields.map(f => ({ name: f.field_name, label: f.field_label, object: f.object_name }))
  });
  console.log('Grouped fields:', groupedFields);

  // Filter out grouped fields from selectedFields to avoid duplication
  const nonGroupedFields = selectedFields.filter(field =>
    !grouping.groupedFields.some(groupField =>
      groupField.field_name === field.field_name &&
      groupField.object_name === field.object_name
    )
  );

  const displayNonGroupedFields = buildDisplayFieldsWithFormulas(
    nonGroupedFields,
    formulaFields
  );
  const singleRowGroup = groupedFields.length === 1;
  const hasSummarizedMeasures = displayNonGroupedFields.some(
    (field) =>
      field.is_formula ||
      (summarizedFields[field.id] && summarizedFields[field.id].aggregation)
  );

  const renderSubtotalMeasureCell = (field, groupRows, leafGroupKey) => {
    if (field.is_formula) {
      const ff = (formulaFields || []).find((f) => f.field_name === field.field_name);
      const label =
        singleRowGroup && leafGroupKey != null
          ? leafGroupKey
          : null;
      const raw =
        label && formulaSummarizedData
          ? getFormulaValueForGroup(formulaSummarizedData, ff, label)
          : null;
      const text = formatFormulaDisplayValue(raw, field.data_type);
      return (
        <div
          key={field.id}
          className="subtotal-data-cell formula-data-cell"
          style={{ textAlign: 'right', justifyContent: 'flex-end' }}
        >
          {text}
        </div>
      );
    }

    const summarizedField = summarizedFields[field.id];
    if (summarizedField) {
      const aggregatedValue = calculateReportAggregation(
        groupRows,
        field,
        summarizedField.aggregation,
        baseCurrencyCode,
        aggregationFormatter,
        exchangeRateLookup,
        primaryObjectName
      );
      return (
        <div
          key={field.id}
          className="subtotal-data-cell"
          style={{
            textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(
              field.data_type?.toLowerCase()
            )
              ? 'right'
              : 'left',
            justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(
              field.data_type?.toLowerCase()
            )
              ? 'flex-end'
              : 'flex-start',
          }}
        >
          {aggregatedValue}
        </div>
      );
    }

    return <div key={field.id} className="subtotal-empty-cell"></div>;
  };

  console.log('Non grouped fields:', nonGroupedFields);

  const getGroupValueForRow = (field, row) => {
    const bucket = getReportGroupBucket(field, row, dateGroupOptions);
    return bucket.label;
  };

  // Helper function to get field value from row (detail cells; non-group columns)
  const getFieldValue = (field, row) => {
    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;
    const fieldData = row[aliasedFieldName] || row[field.field_name];

    console.log(`Getting field value for ${field.field_name}:`, {
      tableName,
      aliasedFieldName,
      fieldData,
      fieldDataType: typeof fieldData,
      availableKeys: Object.keys(row)
    });

    // Handle null/undefined values
    if (fieldData === null || fieldData === undefined) {
      return 'N/A';
    }

    const dt = (field.data_type || '').toLowerCase();

    // Handle object values (wrapped data from backend)
    if (typeof fieldData === 'object' && fieldData !== null) {
      // If it's a wrapped object with value and displayValue properties
      if (fieldData.hasOwnProperty('value') && fieldData.hasOwnProperty('displayValue')) {
        // For checkbox/boolean fields, use the displayValue if available, otherwise convert the value
        if (typeof fieldData.value === 'boolean') {
          return fieldData.displayValue || (fieldData.value ? 'true' : 'false');
        }
        if (dt === 'date' && fieldData.value != null && fieldData.value !== '') {
          return formatDate(fieldData.value);
        }
        if (dt === 'datetime' && fieldData.value != null && fieldData.value !== '') {
          return formatDateTime(fieldData.value);
        }
        // For other fields, use displayValue if available, otherwise use value
        return fieldData.displayValue || String(fieldData.value) || 'N/A';
      }
      // If it's a regular object, try to extract displayValue or value
      const inner = fieldData.displayValue || fieldData.value;
      if (dt === 'date' && inner != null && inner !== '') {
        return formatDate(inner);
      }
      if (dt === 'datetime' && inner != null && inner !== '') {
        return formatDateTime(inner);
      }
      return String(inner || 'N/A');
    }

    // Handle boolean values (including false) - raw boolean values
    if (typeof fieldData === 'boolean') {
      return fieldData ? 'true' : 'false';
    }

    if (dt === 'date' && fieldData != null && fieldData !== '') {
      return formatDate(fieldData);
    }
    if (dt === 'datetime' && fieldData != null && fieldData !== '') {
      return formatDateTime(fieldData);
    }

    // Handle all other types (string, number, etc.)
    return String(fieldData);
  };

  // Simple groupByFields function
  function groupByFields(data, groupFields) {
    console.log('groupByFields called with:', {
      dataLength: data.length,
      groupFields: groupFields.map(f => ({ name: f.field_name, label: f.field_label, object: f.object_name })),
      sampleRow: data.length > 0 ? data[0] : 'No data'
    });

    //let currentLevel = [];

    return data.reduce((acc, row) => {
      let currentLevel = acc;
      console.log('currentLevel: ', currentLevel);
      console.log("row: ", row);
      groupFields.forEach((field, index) => {
        const fieldValue = getGroupValueForRow(field, row);
        console.log(`${index} -->Processing field ${field.field_name}, got value:`, fieldValue);

        // if value not initialized, create new object/array
        if (!currentLevel[fieldValue]) {
          currentLevel[fieldValue] = (index === groupFields.length - 1) ? [] : {};
        }

        // go deeper
        if (index === groupFields.length - 1) {
          currentLevel[fieldValue].push(row);
        } else {
          currentLevel = currentLevel[fieldValue];
        }
      });

      return acc;
    }, {});
  }

  const nestedGroups = groupByFields(data, groupedFields);

  // When SQL sort includes a grouped column, the server only orders by group keys.
  // Apply additional sort keys (non-grouped columns) inside each leaf bucket only.
  const groupIds = new Set(
    (grouping.groupedFields || []).map(g => g.id).filter(Boolean)
  );
  const sortIncludesGroupedColumn =
    Array.isArray(reportSortKeys) &&
    reportSortKeys.some(k => k.field_config_id && groupIds.has(k.field_config_id));
  const withinGroupSortKeys = (reportSortKeys || []).filter(
    k => k.field_config_id && !groupIds.has(k.field_config_id)
  );

  const resolveFieldForSortKey = key => {
    if (!key?.field_config_id) return null;
    return (
      selectedFields.find(
        f =>
          f.id === key.field_config_id ||
          f.field_config_id === key.field_config_id
      ) ||
      (grouping.groupedFields || []).find(
        g =>
          g.id === key.field_config_id ||
          g.field_config_id === key.field_config_id
      )
    );
  };

  function sortLeafRowsInPlace(node, depth) {
    if (!sortIncludesGroupedColumn || withinGroupSortKeys.length === 0) return;
    if (depth === groupedFields.length - 1) {
      if (!Array.isArray(node)) return;
      node.sort((rowA, rowB) => {
        for (const key of withinGroupSortKeys) {
          const field = resolveFieldForSortKey(key);
          const va = getReportRowFieldSortComparable(
            field,
            rowA,
            primaryObjectName
          );
          const vb = getReportRowFieldSortComparable(
            field,
            rowB,
            primaryObjectName
          );
          const c = compareReportSortValues(va, vb);
          if (c !== 0) {
            return key.direction === 'DESC' ? -c : c;
          }
        }
        return 0;
      });
      return;
    }
    if (node && typeof node === 'object' && !Array.isArray(node)) {
      Object.keys(node).forEach(childKey => {
        sortLeafRowsInPlace(node[childKey], depth + 1);
      });
    }
  }

  sortLeafRowsInPlace(nestedGroups, 0);

  console.log('Nested groups:', nestedGroups);

  // Create printedGroups and calculatedGroup objects
  const printedGroups = {};
  const calculatedGroup = {};

  // First, initialize all group keys from nestedGroups with hierarchical composite keys
  const initializeGroups = (groups, fieldIndex = 0, parentPath = '') => {
    for (const key in groups) {
      const value = groups[key];
      const field = groupedFields[fieldIndex];
      const currentPath = parentPath ? `${parentPath}|${key}` : key;
      const compositeKey = `${field.field_name}_${currentPath}`;

      if (Array.isArray(value)) {
        // This is the final level - just add the composite key
        printedGroups[compositeKey] = false;
        calculatedGroup[compositeKey] = 0; // Initialize to 0
      } else if (typeof value === 'object' && value !== null) {
        // This is a nested object - add the composite key and recurse
        printedGroups[compositeKey] = false;
        calculatedGroup[compositeKey] = 0; // Initialize to 0
        initializeGroups(value, fieldIndex + 1, currentPath);
      }
    }
  };

  initializeGroups(nestedGroups);

  // Helper function to find the hierarchical path for a row in nestedGroups
  const findRowPath = (row, groups, fieldIndex = 0, currentPath = '') => {
    const field = groupedFields[fieldIndex];
    const fieldValue = getGroupValueForRow(field, row);

    if (fieldValue === 'N/A' || !groups[fieldValue]) {
      return null;
    }

    const newPath = currentPath ? `${currentPath}|${fieldValue}` : fieldValue;

    if (fieldIndex === groupedFields.length - 1) {
      // This is the last field, check if this row exists in the array
      const rows = groups[fieldValue];
      if (Array.isArray(rows) && rows.includes(row)) {
        return newPath;
      }
    } else {
      // Continue to next level
      return findRowPath(row, groups[fieldValue], fieldIndex + 1, newPath);
    }

    return null;
  };

  // Now iterate over all data to fill the counts using hierarchical composite keys
  data.forEach((row, rowIndex) => {
    const rowPath = findRowPath(row, nestedGroups);
    if (rowPath && typeof rowPath === 'string') {
      groupedFields.forEach((field, fieldIndex) => {
        const fieldValue = getGroupValueForRow(field, row);
        if (fieldValue) {
          // Create path up to current field level
          const pathParts = rowPath.split('|');
          const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
          const compositeKey = `${field.field_name}_${currentPath}`;

          if (calculatedGroup.hasOwnProperty(compositeKey)) {
            calculatedGroup[compositeKey]++;
            console.log(`Row ${rowIndex}: Field ${field.field_name} = "${fieldValue}", path: "${currentPath}", composite key: "${compositeKey}", count now: ${calculatedGroup[compositeKey]}`);
          }
        }
      });
    }
  });

  console.log('Printed groups initialized:', printedGroups);
  console.log('Calculated groups initialized:', calculatedGroup);

  // Calculate grand total
  const grandTotal = data.length;

  // If no groups were created, show error message
  if (!nestedGroups || Object.keys(nestedGroups).length === 0) {
    console.error('No groups created. Data:', data, 'Grouped fields:', groupedFields);
    return (
      <div>
        <div style={{ color: 'red', padding: '10px' }}>
          Error: No groups could be created. Check console for details.
        </div>
        <div style={{ padding: '10px' }}>
          <strong>Debug Info:</strong>
          <br />Data length: {data.length}
          <br />Grouped fields: {groupedFields.map(f => f.field_name).join(', ')}
          <br />Sample data: {JSON.stringify(data[0] || {}, null, 2)}
        </div>
      </div>
    );
  }

  // Track subtotals for each group
  const groupSubtotals = {};

  // Render nested grouped data
  const renderNestedGroups = (groups, level = 0, parentKey = '') => {
    if (!groups || typeof groups !== 'object') {
      console.error('Invalid groups in renderNestedGroups:', groups, 'level:', level);
      return <div>Error: Invalid groups data</div>;
    }

    const isLastLevel = level === groupedFields.length - 1;
    console.log("isLastLevel: ", isLastLevel, "level: ", level, "parentKey: ", parentKey);

    const results = [];

    const groupFieldAtLevel = groupedFields[level];
    const groupKeys = groupFieldAtLevel
      ? sortReportGroupKeys(
          Object.keys(groups),
          groupFieldAtLevel,
          reportFieldsForSort,
          picklistValuesByFieldId
        )
      : Object.keys(groups);

    for (const key of groupKeys) {
      const value = groups[key];
      console.log("key: ", key, "value: ", value);

      if (Array.isArray(value)) {
        // Last level → render rows
        const groupKey = parentKey ? `${parentKey}|${key}` : key;

        results.push(
          <div key={groupKey} className="grouped-section">
            {hasSummarizedMeasures ? (
              <div className="data-row group-summary-row">
                {groupedFields.map((field, fieldIndex) => (
                  <div
                    key={field.id}
                    className="data-cell grouped-field-cell"
                    style={{
                      textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(
                        field.data_type?.toLowerCase()
                      )
                        ? 'right'
                        : 'left',
                      justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(
                        field.data_type?.toLowerCase()
                      )
                        ? 'flex-end'
                        : 'flex-start',
                    }}
                  >
                    {fieldIndex === level ? `${key} (${value.length})` : ''}
                  </div>
                ))}
                {displayNonGroupedFields.map((field) =>
                  renderSubtotalMeasureCell(field, value, key)
                )}
              </div>
            ) : (
              <>
            {value.map((row, index) => (
              <div key={index} className="data-row grouped-row">
                {groupedFields.map((field, fieldIndex) => {
                  const currentValue = getGroupValueForRow(field, row);
                  let shouldShowValue = false;
                  let displayValue = '';

                  // Create a hierarchical composite key for this group value
                  // We need to determine the path up to this field level
                  const rowPath = findRowPath(row, nestedGroups);
                  let compositeKey = `${field.field_name}_${currentValue}`;

                  if (rowPath && typeof rowPath === 'string' && fieldIndex > 0) {
                    // For nested fields, use the hierarchical path
                    const pathParts = rowPath.split('|');
                    const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
                    compositeKey = `${field.field_name}_${currentPath}`;
                  }

                  console.log("compositeKey: ", compositeKey, "printedGroups[compositeKey]: ", printedGroups[compositeKey]);
                  // Show value only if not printed before
                  if (printedGroups[compositeKey] === false) {
                    shouldShowValue = true;
                    printedGroups[compositeKey] = true;

                    // Get count from calculatedGroup using composite key
                    const count = calculatedGroup[compositeKey] || 0;

                    console.log(`Group count for ${currentValue}:`, {
                      compositeKey,
                      count,
                      calculatedGroup: calculatedGroup[compositeKey]
                    });

                    displayValue = `${currentValue} (${count})`;
                  }

                  return (
                    <div
                      key={field.id}
                      className="data-cell grouped-field-cell"
                      style={{
                        textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                        justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {shouldShowValue ? displayValue : '--'}
                    </div>
                  );
                })}
                {/* Data for non-grouped fields */}
                {displayNonGroupedFields.map((field) => {
                  if (field.is_formula) {
                    return (
                      <div
                        key={field.id}
                        className="data-cell formula-data-cell"
                        style={{ textAlign: 'right', justifyContent: 'flex-end' }}
                      >
                        —
                      </div>
                    );
                  }
                  const tableName = field.object_name || 'main';
                  const aliasedFieldName = `${tableName}_${field.field_name}`;
                  const fieldData = row[aliasedFieldName] || row[field.field_name];
                  return (
                    <div
                      key={field.id}
                      className="data-cell"
                      style={{
                        textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                        justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {formatDataCell(fieldData, field, row)}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Subtotal row for this group */}
            <div className="subtotal-row">
              {/* Empty cells for all grouped fields except the current level which shows Subtotal */}
              {groupedFields.map((field, fieldIndex) => (
                <div key={field.id} className="subtotal-cell">
                  {fieldIndex === level ? <span className="subtotal-label">Subtotal</span> : ''}
                </div>
              ))}
              {/* Show aggregated values for summarized fields, empty cells for others */}
              {displayNonGroupedFields.map((field) =>
                renderSubtotalMeasureCell(field, value, key)
              )}
            </div>
              </>
            )}
          </div>
        );
      } else {
        // Not last level → recursively render sub-groups
        const subResults = renderNestedGroups(value, level + 1, parentKey ? `${parentKey}|${key}` : key);
        console.log("subResults: ", subResults);
        results.push(...subResults);

        // Add subtotal for this group level
        const subtotalKey = `subtotal_${parentKey}_${key}_${level}`;
        results.push(
          <div key={subtotalKey} className="subtotal-row">
            {/* Empty cells for all grouped fields except the current level which shows Subtotal */}
            {groupedFields.map((field, fieldIndex) => (
              <div key={field.id} className="subtotal-cell">
                {fieldIndex === level ? <span className="subtotal-label">Subtotal</span> : ''}
              </div>
            ))}
            {/* Show aggregated values for summarized fields, empty cells for others */}
            {displayNonGroupedFields.map((field) => {
              const getAllRows = (obj) => {
                let allRows = [];
                for (const subKey in obj) {
                  const subValue = obj[subKey];
                  if (Array.isArray(subValue)) {
                    allRows = allRows.concat(subValue);
                  } else if (typeof subValue === 'object' && subValue !== null) {
                    allRows = allRows.concat(getAllRows(subValue));
                  }
                }
                return allRows;
              };
              const groupData = getAllRows(value);
              return renderSubtotalMeasureCell(
                field,
                groupData,
                singleRowGroup ? key : null
              );
            })}
          </div>
        );
      }

    }

    return results;
  }


  return (
    <>
      {renderNestedGroups(nestedGroups)}

      {/* Grand total row */}
      <div className="data-row grand-total-row">
        {/* Empty cells for all grouped fields except the last one which shows Grand Total */}
        {groupedFields.map((field, fieldIndex) => (
          <div key={field.id} className="data-cell grand-total-cell">
            {fieldIndex === groupedFields.length - 1 ? <span className="grand-total-label">Grand Total ({grandTotal})</span> : ''}
          </div>
        ))}
        {/* Show aggregated values for summarized fields, empty cells for others */}
        {displayNonGroupedFields.map((field) => {
          if (field.is_formula) {
            return <div key={field.id} className="data-cell grand-total-empty-cell" />;
          }
          const summarizedField = summarizedFields[field.id];
          if (summarizedField) {
            const aggregatedValue = calculateReportAggregation(
              data,
              field,
              summarizedField.aggregation,
              baseCurrencyCode,
              aggregationFormatter,
              exchangeRateLookup,
              primaryObjectName
            );
            return (
              <div
                key={field.id}
                className="data-cell grand-total-data-cell"
                style={{
                  textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left',
                  justifyContent: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'flex-end' : 'flex-start'
                }}
              >
                {aggregatedValue}
              </div>
            );
          }
          return <div key={field.id} className="data-cell grand-total-empty-cell" />;
        })}
      </div>
    </>
  );
};

const ReportPreview = ({
  reportData,
  selectedFields,
  selectedGroups,
  selectedColumnGroups = [],
  matrixDateGranularity = 'month',
  onMatrixDateGranularityChange,
  matrixGrid = null,
  previewData,
  filters,
  previewLoading = false,
  grouping = null,
  onColumnAction = () => { },
  summarizedFields = {},
  summarizedData = {}, // Add summarizedData prop
  onSummarizedDataChange = () => { }, // New prop to pass summarized data back
  charts = [], // New prop for charts
  chartsVersion = 0, // Version to force re-render when charts change
  onEditChart = () => { }, // Edit chart handler
  onDeleteChart = () => { }, // Delete chart handler
  onViewChart = () => { }, // View chart handler
  reportSortKeys = [], // report.config.sort — multi-level ORDER BY
  formulaFields = [],
}) => {
  const primaryObjectName = useMemo(() => {
    if (!reportData) return null;
    if (reportData.type === 'individual') return reportData.object_name || null;
    // Relationship / summary report types store the primary object under source_object_name
    return reportData.source_object_name || reportData.object_name || null;
  }, [reportData]);

  const {
    reportConversionBaseCode,
    reportConversionLookup,
    multiCurrencyEnabled,
    currencyReady,
  } = useReportCurrency();

  useEffect(() => {
    logReportCurrency('ReportPreview.currency', {
      currencyReady,
      multiCurrencyEnabled,
      reportConversionBaseCode,
      hasLookup: Boolean(reportConversionLookup),
      lookupKeyCount: reportConversionLookup
        ? Object.keys(reportConversionLookup).length
        : 0,
      matrixMode: isMatrixMode(selectedColumnGroups),
      previewRowCount: previewData?.length ?? 0,
    });
  }, [
    currencyReady,
    multiCurrencyEnabled,
    reportConversionBaseCode,
    reportConversionLookup,
    selectedColumnGroups,
    previewData?.length,
  ]);

  useEffect(() => {
    if (!isMatrixMode(selectedColumnGroups) || !selectedFields?.length) return;
    logReportCurrencyTable('ReportPreview.selectedFields', selectedFields.map(f => ({
      id: f.id,
      field_name: f.field_name,
      data_type: f.data_type,
      summarized: summarizedFields[f.id]?.aggregation || summarizedFields[f.field_config_id]?.aggregation || '—',
      isCurrencyLike: reportFieldIsCurrencyLike(f),
    })));
    logReportCurrency('ReportPreview.summarizedFieldsKeys', {
      keys: Object.keys(summarizedFields || {}),
      entries: Object.entries(summarizedFields || {}).map(([k, v]) => ({
        key: k,
        aggregation: v?.aggregation,
      })),
    });
  }, [selectedColumnGroups, selectedFields, summarizedFields]);

  useEffect(() => {
    if (!previewData?.length) return;
    const row = previewData[0];
    logReportCurrency('ReportPreview.previewDataSample', {
      rowCount: previewData.length,
      rowKeys: Object.keys(row).slice(0, 30),
      currencyRelated: Object.fromEntries(
        Object.entries(row).filter(([k]) =>
          /currency|exchange|amount/i.test(k)
        )
      ),
      matrixGridFromApi: Boolean(matrixGrid),
    });
  }, [previewData, matrixGrid]);

  const aggregationFormatter = useMemo(
    () => makePreviewAggregationFormat(reportConversionBaseCode),
    [reportConversionBaseCode]
  );

  const formatPreviewDataCell = (fieldData, field, row) => {
    const money = formatReportCurrencyCell(
      fieldData,
      field,
      row,
      reportConversionBaseCode,
      resolveCurrency('INR'),
      reportConversionLookup
    );
    if (money != null) return money;
    const calculated = formatReportCalculatedCell(
      fieldData,
      field,
      row,
      reportConversionBaseCode || resolveTokenCurrency('INR')
    );
    if (calculated != null) return calculated;
    return formatValue(
      fieldData,
      field.data_type,
      field.display_config || field.displayConfig,
      row,
      field
    );
  };

  // Debug: Log the summarized fields to see what's being passed
  console.log('ReportPreview received summarizedFields:', summarizedFields);

  // Debug: Log charts data to track updates
  console.log('ReportPreview received charts:', charts);
  console.log('ReportPreview selectedFields:', selectedFields);
  console.log('ReportPreview selectedGroups:', selectedGroups);
  console.log('ReportPreview grouping:', grouping);

  // Function to extract summarized data for charts
  // Helper function to get field ID from various possible properties
  const getFieldId = (field) => {
    return field.id || field.field_id || field.field_config_id || `${field.object_name}_${field.field_name}`;
  };

  const sqlSortInfoForField = (field) => {
    if (!reportSortKeys?.length || !field) return null;
    const id = getFieldId(field);
    const idx = reportSortKeys.findIndex(k => k.field_config_id === id);
    if (idx < 0) return null;
    return {
      level: idx + 1,
      direction: reportSortKeys[idx].direction === 'DESC' ? 'desc' : 'asc',
    };
  };

  const sqlSortLevelsCount = reportSortKeys.length;

  const extractSummarizedData = () => {
    if (!previewData || previewData.length === 0) {
      return {};
    }

    if (isMatrixMode(selectedColumnGroups)) {
      return buildSummarizedDataFromMatrix({
        previewData,
        rowGroups: selectedGroups,
        columnGroups: selectedColumnGroups,
        selectedFields,
        summarizedFields,
        matrixDateGranularity,
        baseCurrencyCode: reportConversionBaseCode,
        exchangeRateLookup: reportConversionLookup,
        multiCurrencyEnabled,
      });
    }

    // Check if we have grouping from either source
    const hasGroupingFromAPI = grouping && grouping.hasGrouping;
    const hasGroupingFromSelected = selectedGroups && selectedGroups.length > 0;

    if (!hasGroupingFromAPI && !hasGroupingFromSelected) {
      return {};
    }

    const previewAggregationFormatter = makePreviewAggregationFormat(reportConversionBaseCode);
    const dateGroupOptions = buildDateGroupOptions(matrixDateGranularity);

    const summarizedData = {};

    // Get grouped fields - prioritize selectedGroups (frontend) over API response
    let groupedFields = [];
    if (hasGroupingFromSelected) {
      // Use selectedGroups which contains the field IDs directly
      groupedFields = selectedGroups;
      console.log('Using selectedGroups for grouped fields:', groupedFields);
    } else if (hasGroupingFromAPI) {
      // Fallback to API response
      groupedFields = grouping.groupedFields || [];
      console.log('Using API grouping for grouped fields:', groupedFields);
    }

    const fieldsWithAggregation = collectSummarizedMeasureFields(
      selectedFields,
      summarizedFields,
      groupedFields
    );

    // Add summarized field metadata to the structure
    if (fieldsWithAggregation.length > 0) {
      if (!summarizedData._metadata) {
        summarizedData._metadata = {
          group_fields: {},
          summarized_fields: {}
        };
      }

      fieldsWithAggregation.forEach(field => {
        summarizedData._metadata.summarized_fields[field.field_name] = {
          field_id: getFieldId(field),
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          field_type: field.field_type,
          is_required: field.is_required,
          is_unique: field.is_unique,
          default_value: field.default_value,
          field_length: field.field_length,
          decimal_places: field.decimal_places,
          picklist_values: field.picklist_values,
          lookup_object: field.lookup_object,
          lookup_field: field.lookup_field,
          help_text: field.help_text,
          description: field.description,
          aggregation: field.aggregation,
          ...(field.calculatedReturnType
            ? { calculatedReturnType: field.calculatedReturnType }
            : {}),
        };
      });
    }

    console.log('Extracting summarized data:', {
      groupedFields: groupedFields.map(f => f.field_name),
      fieldsWithAggregation: fieldsWithAggregation.map(f => ({ name: f.field_name, aggregation: f.aggregation }))
    });

    // For each grouped field, create a map of group values to aggregated values
    groupedFields.forEach(groupField => {
      const groupFieldName = groupField.field_name;

      console.log('Processing group field:', {
        groupFieldName,
        groupField: groupField,
        fieldId: groupField.id,
        field_id: groupField.field_id,
        field_config_id: groupField.field_config_id
      });

      // Add group field metadata to the summarized data structure
      if (!summarizedData._metadata) {
        summarizedData._metadata = {
          group_fields: {},
          summarized_fields: {}
        };
      }

      // Store group field metadata
      summarizedData._metadata.group_fields[groupFieldName] = {
        field_id: getFieldId(groupField),
        field_name: groupField.field_name,
        field_label: groupField.field_label,
        data_type: groupField.data_type,
        object_name: groupField.object_name,
        field_type: groupField.field_type,
        is_required: groupField.is_required,
        is_unique: groupField.is_unique,
        default_value: groupField.default_value,
        field_length: groupField.field_length,
        decimal_places: groupField.decimal_places,
        picklist_values: groupField.picklist_values,
        lookup_object: groupField.lookup_object,
        lookup_field: groupField.lookup_field,
        help_text: groupField.help_text,
        description: groupField.description
      };
      const groupValues = new Set();

      // Collect all unique group values
      previewData.forEach((row) => {
        const { label } = getReportGroupBucket(groupField, row, dateGroupOptions);
        groupValues.add(label || 'N/A');
      });

      // For each group value, calculate aggregated values for each summarized field
      summarizedData[groupFieldName] = {};

      if (groupFieldName === groupedFields[0]?.field_name) {
        fieldsWithAggregation.forEach((field) => {
          if (!summarizedData[field.field_name]) {
            summarizedData[field.field_name] = {};
          }
        });
      }

      groupValues.forEach(groupValue => {
        summarizedData[groupFieldName][groupValue] = {};

        // Filter rows for this group value
        const rowsForGroup = previewData.filter((row) => {
          const { label } = getReportGroupBucket(groupField, row, dateGroupOptions);
          return (label || 'N/A') === groupValue;
        });

        // Calculate aggregated values for each summarized field
        fieldsWithAggregation.forEach(summarizedField => {
          const aggregation = summarizedField.aggregation;
          const aggregatedValue = calculateReportAggregation(
            rowsForGroup,
            summarizedField,
            aggregation,
            reportConversionBaseCode,
            previewAggregationFormatter,
            reportConversionLookup,
            primaryObjectName
          );
          const n = getAggregationNumericResult(
            rowsForGroup,
            summarizedField,
            aggregation,
            reportConversionBaseCode,
            reportConversionLookup,
            primaryObjectName
          );
          let chartValue = aggregatedValue;
          if (n != null) {
            chartValue = n;
          } else if (
            (summarizedField.data_type === 'currency' ||
              reportFieldIsPercentLike(summarizedField)) &&
            typeof aggregatedValue === 'string'
          ) {
            const numericValue = parseFloat(
              String(aggregatedValue).replace(/[^0-9.-]/g, '')
            );
            chartValue = Number.isNaN(numericValue) ? 0 : numericValue;
          } else if (
            summarizedField.data_type === 'number' ||
            summarizedField.data_type === 'decimal' ||
            summarizedField.data_type === 'percent'
          ) {
            const numericValue = parseFloat(aggregatedValue);
            chartValue = Number.isNaN(numericValue) ? 0 : numericValue;
          }
          if (groupFieldName === groupedFields[0]?.field_name) {
            summarizedData[summarizedField.field_name][groupValue] = chartValue;
          }
          summarizedData[groupFieldName][groupValue][summarizedField.field_name] = {
            value: aggregatedValue,
            aggregation: aggregation,
            field_label: summarizedField.field_label,
            field_id: getFieldId(summarizedField),
            field_name: summarizedField.field_name,
            data_type: summarizedField.data_type,
            object_name: summarizedField.object_name,
            field_type: summarizedField.field_type,
            is_required: summarizedField.is_required,
            is_unique: summarizedField.is_unique,
            default_value: summarizedField.default_value,
            field_length: summarizedField.field_length,
            decimal_places: summarizedField.decimal_places,
            picklist_values: summarizedField.picklist_values,
            lookup_object: summarizedField.lookup_object,
            lookup_field: summarizedField.lookup_field,
            help_text: summarizedField.help_text,
            description: summarizedField.description
          };
        });
      });
    });

    // Add additional metadata about the report and data structure
    if (!summarizedData._metadata) {
      summarizedData._metadata = {
        group_fields: {},
        summarized_fields: {}
      };
    }

    summarizedData._metadata.report_info = {
      total_records: previewData.length,
      group_count: groupedFields.length,
      summarized_field_count: fieldsWithAggregation.length,
      extraction_timestamp: new Date().toISOString(),
      data_structure_version: '2.0',
      report_type: isMatrixMode(selectedColumnGroups) ? 'matrix' : 'summary',
    };

    if (groupedFields.length > 0) {
      const primaryGroup = groupedFields[0];
      const primaryGroupName = primaryGroup.field_name;
      summarizedData.record_count = {};
      if (!summarizedData[primaryGroupName]) {
        summarizedData[primaryGroupName] = {};
      }
      Object.keys(summarizedData[primaryGroupName] || {}).forEach((groupValue) => {
        const rowsForGroup = previewData.filter((row) => {
          const { label } = getReportGroupBucket(primaryGroup, row, dateGroupOptions);
          return (label || 'N/A') === groupValue;
        });
        const count = rowsForGroup.length;
        summarizedData.record_count[groupValue] = count;
        if (!summarizedData[primaryGroupName][groupValue]) {
          summarizedData[primaryGroupName][groupValue] = {};
        }
        summarizedData[primaryGroupName][groupValue].record_count = { value: count };
      });
    }

    summarizedData._metadata.data_summary = {
      available_groups: Object.keys(summarizedData).filter(key => key !== '_metadata'),
      available_summarized_fields: fieldsWithAggregation.map(f => f.field_name),
      sample_data: {}
    };

    // Add sample data for each group
    Object.keys(summarizedData).forEach(groupKey => {
      if (groupKey !== '_metadata') {
        const groupData = summarizedData[groupKey];
        const sampleGroupValue = Object.keys(groupData)[0];
        if (sampleGroupValue) {
          summarizedData._metadata.data_summary.sample_data[groupKey] = {
            sample_group_value: sampleGroupValue,
            available_summarized_fields: Object.keys(groupData[sampleGroupValue])
          };
        }
      }
    });

    console.log('Extracted summarized data with enhanced metadata:', summarizedData);
    return summarizedData;
  };

  // Extract summarized data when previewData, summarizedFields, or grouping changes
  React.useEffect(() => {
    const canExtract =
      previewData &&
      previewData.length > 0 &&
      (isMatrixMode(selectedColumnGroups)
        ? selectedGroups?.length > 0 && selectedColumnGroups?.length > 0
        : Object.keys(summarizedFields).length > 0 ||
          (selectedGroups && selectedGroups.length > 0));

    if (canExtract) {
      const summarizedData = extractSummarizedData();
      onSummarizedDataChange(summarizedData);
    }
  }, [
    previewData,
    summarizedFields,
    grouping,
    selectedGroups,
    selectedColumnGroups,
    matrixDateGranularity,
    reportConversionBaseCode,
    reportConversionLookup,
    multiCurrencyEnabled,
    selectedFields,
    formulaFields,
  ]);

  // Debug: Check which fields should be summarized
  selectedFields.forEach(field => {
    const isSummarized = summarizedFields[field.id];
    console.log(`Field ${field.field_name} (ID: ${field.id}): isSummarized = ${!!isSummarized}, aggregation = ${isSummarized?.aggregation || 'none'}`);
  });

  // Helper function to check if a field is grouped
  const isFieldGrouped = (field) => {
    return selectedGroups.some(group => group.id === field.id);
  };

  const isFieldColumnGrouped = (field) => {
    return selectedColumnGroups.some(group => group.id === field.id);
  };

  const matrixMode = isMatrixMode(selectedColumnGroups);

  const reportFieldsForPicklist = useMemo(
    () => [
      ...selectedFields,
      ...selectedGroups,
      ...selectedColumnGroups,
    ],
    [selectedFields, selectedGroups, selectedColumnGroups]
  );

  const groupFieldsForPicklist = useMemo(
    () => [...selectedGroups, ...selectedColumnGroups],
    [selectedGroups, selectedColumnGroups]
  );

  const picklistValuesByFieldId = useReportGroupPicklistValues(
    groupFieldsForPicklist,
    reportFieldsForPicklist
  );

  // Create grouping information from selectedGroups
  const groupingInfo = {
    hasGrouping: selectedGroups.length > 0,
    groupedFields: selectedGroups.map(group => ({
      field_name: group.field_name,
      field_label: group.field_label,
      object_name: group.object_name,
      data_type: group.data_type,
      id: group.id
    }))
  };

  console.log('ReportPreview received props:', {
    selectedFields: selectedFields.length,
    selectedGroups: selectedGroups.length,
    grouping,
    previewDataLength: previewData.length
  });

  console.log('Grouping details:', {
    hasGrouping: grouping?.hasGrouping,
    groupedFields: grouping?.groupedFields,
    groupingType: typeof grouping,
    groupingKeys: grouping ? Object.keys(grouping) : 'null'
  });

  console.log('Created grouping info:', groupingInfo);

  const getResolvedField = (field) => {
    const fieldId = getFieldId(field);

    const matchFromSelected = selectedFields.find(sel => getFieldId(sel) === fieldId);
    if (matchFromSelected) {
      return matchFromSelected;
    }

    const matchFromGroups = selectedGroups.find(group => getFieldId(group) === fieldId);
    if (matchFromGroups) {
      return matchFromGroups;
    }

    return {
      ...field,
      id: fieldId
    };
  };

  const getFieldSummarization = (field) => {
    const fieldId = getFieldId(field);
    return summarizedFields[fieldId];
  };

  const tabularColumnCount = useMemo(() => {
    if (groupingInfo.hasGrouping) {
      const nonGroupedFields = selectedFields.filter(
        (field) =>
          !groupingInfo.groupedFields.some(
            (groupField) =>
              groupField.field_name === field.field_name &&
              groupField.object_name === field.object_name
          )
      );
      return (
        groupingInfo.groupedFields.length +
        buildDisplayFieldsWithFormulas(nonGroupedFields, formulaFields).length
      );
    }
    return selectedFields.length;
  }, [selectedFields, groupingInfo, formulaFields]);

  const tabularGridStyle = useMemo(
    () =>
      tabularColumnCount > 0
        ? {
            gridTemplateColumns: `repeat(${tabularColumnCount}, minmax(120px, auto))`,
          }
        : undefined,
    [tabularColumnCount]
  );

  const renderTabularHeaderCells = () => {
    if (groupingInfo.hasGrouping) {
      return (
        <>
          {groupingInfo.groupedFields.map((field) => {
            const resolvedField = getResolvedField(field);
            const fieldKey = getFieldId(resolvedField);
            const summaryInfo = getFieldSummarization(resolvedField);

            return (
              <div key={fieldKey} className="header-cell grouped-header">
                <ColumnHeaderDropdown
                  field={resolvedField}
                  onSort={onColumnAction}
                  onGroup={onColumnAction}
                  onSummarize={onColumnAction}
                  isSummarized={!!summaryInfo}
                  aggregationType={summaryInfo?.aggregation}
                  isGrouped={true}
                  isColumnGrouped={isFieldColumnGrouped(resolvedField)}
                  sqlSortInfo={sqlSortInfoForField(resolvedField)}
                  sqlSortLevelsCount={sqlSortLevelsCount}
                />
              </div>
            );
          })}
          {buildDisplayFieldsWithFormulas(
            selectedFields.filter(
              (field) =>
                !groupingInfo.groupedFields.some(
                  (groupField) =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                )
            ),
            formulaFields
          ).map((field) => {
            if (field.is_formula) {
              return (
                <div key={field.id} className="header-cell formula-header-cell">
                  <span className="header-label">{field.field_label}</span>
                </div>
              );
            }

            const resolvedField = getResolvedField(field);
            const fieldKey = getFieldId(resolvedField);
            const summaryInfo = getFieldSummarization(resolvedField);

            return (
              <div key={fieldKey} className="header-cell">
                <ColumnHeaderDropdown
                  field={resolvedField}
                  onSort={onColumnAction}
                  onGroup={onColumnAction}
                  onSummarize={onColumnAction}
                  isSummarized={!!summaryInfo}
                  aggregationType={summaryInfo?.aggregation}
                  isGrouped={isFieldGrouped(resolvedField)}
                  isColumnGrouped={isFieldColumnGrouped(resolvedField)}
                  sqlSortInfo={sqlSortInfoForField(resolvedField)}
                  sqlSortLevelsCount={sqlSortLevelsCount}
                />
              </div>
            );
          })}
        </>
      );
    }

    return selectedFields.map((field) => {
      const resolvedField = getResolvedField(field);
      const fieldKey = getFieldId(resolvedField);
      const summaryInfo = getFieldSummarization(resolvedField);

      return (
        <div key={fieldKey} className="header-cell">
          <ColumnHeaderDropdown
            field={resolvedField}
            onSort={onColumnAction}
            onGroup={onColumnAction}
            onSummarize={onColumnAction}
            isSummarized={!!summaryInfo}
            aggregationType={summaryInfo?.aggregation}
            isGrouped={isFieldGrouped(resolvedField)}
            isColumnGrouped={isFieldColumnGrouped(resolvedField)}
            sqlSortInfo={sqlSortInfoForField(resolvedField)}
            sqlSortLevelsCount={sqlSortLevelsCount}
          />
        </div>
      );
    });
  };

  return (
    <div className="preview-panel">
      <div className="preview-banner">
        <span className="preview-message">
          Previewing a limited number of records. Run the report to see everything.
        </span>
      </div>

      {previewLoading ? (
        <div className="preview-loading">
          <div className="spinner"></div>
          <p>Loading preview data...</p>
        </div>
      ) : selectedFields.length > 0 ? (
        <div className="report-preview">
          {/* Charts Section */}
          {charts && charts.length > 0 && (
            <div className="charts-preview-section">
              <div className="charts-preview-header">
                <h4>Charts</h4>
              </div>
              <div className={`charts-preview-grid ${charts.length === 1 ? 'single-chart' : ''}`} key={`charts-grid-${chartsVersion}-${charts.length}-${charts.map(c => `${c.id}-${c.sortOrder}-${c.columnSpan}`).join('-')}`}>
                {charts
                  .sort((a, b) => (a.sortOrder || 1) - (b.sortOrder || 1)) // Sort charts by sortOrder
                  .map((chart) => {
                    // Use columnSpan from chart configuration
                    const getChartLayoutClass = (columnSpan) => {
                      if (columnSpan === 2) return 'chart-full-width';
                      return 'chart-half';
                    };

                    const layoutClass = getChartLayoutClass(chart.columnSpan || 1);

                    return (
                      <div
                        key={`${chart.id}-${chart.sortOrder}-${chart.columnSpan}-${chartsVersion}`}
                        className={`chart-wrapper ${layoutClass}`}
                      >
                        <ChartRenderer
                          key={`chart-display-${chart.id}-${chart.sortOrder}-${chart.columnSpan}-${chartsVersion}-${reportConversionBaseCode || 'nc'}`}
                          chart={normalizeReportChartForDisplay(chart, summarizedData)}
                          selectedFields={selectedFields}
                          summarizedFields={summarizedFields}
                          selectedGroups={selectedGroups}
                          selectedColumnGroups={selectedColumnGroups}
                          summarizedData={summarizedData}
                          previewData={previewData}
                          matrixDateGranularity={matrixDateGranularity}
                          baseCurrencyCode={reportConversionBaseCode}
                          exchangeRateLookup={reportConversionLookup}
                          multiCurrencyEnabled={multiCurrencyEnabled}
                          onEdit={onEditChart}
                          onDelete={onDeleteChart}
                          onView={onViewChart}
                          isPreview={false}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          )}


          {matrixMode ? (
            previewData.length > 0 ? (
              <div className="report-data">
                <MatrixReportTable
                  key={`matrix-${currencyReady ? reportConversionBaseCode || 'ready' : 'loading'}-${previewData.length}-${formulaFields.length}`}
                  previewData={previewData}
                  rowGroups={selectedGroups}
                  columnGroups={selectedColumnGroups}
                  selectedFields={selectedFields}
                  summarizedFields={summarizedFields}
                  matrixDateGranularity={matrixDateGranularity}
                  onMatrixDateGranularityChange={onMatrixDateGranularityChange}
                  baseCurrencyCode={reportConversionBaseCode}
                  exchangeRateLookup={reportConversionLookup}
                  multiCurrencyEnabled={multiCurrencyEnabled}
                  recordCount={previewData.length}
                  matrixGrid={matrixGrid}
                  formulaFields={formulaFields}
                  formulaSummarizedData={summarizedData}
                />
              </div>
            ) : (
              <div className="no-data-message">
                <p>No records returned in preview. Try running the report or editing report filters.</p>
                <div className="suggestions">
                  <p>• Show <a href="#">All accounts</a>.</p>
                  <p>• Set the Created Date filter to <a href="#">All Time</a>.</p>
                  <p>• <a href="#">Edit other filters</a> in the filter panel.</p>
                </div>
              </div>
            )
          ) : (
            <div className="report-tabular-grid" style={tabularGridStyle}>
              <div className="report-headers">
                {renderTabularHeaderCells()}
              </div>
              {previewData.length > 0 ? (
                groupingInfo.hasGrouping ? (
                  <>
                    {matrixConfigHasDateGroups(selectedGroups, selectedColumnGroups) && (
                      <div className="report-tabular-full-width">
                        <DateGroupGranularityBar
                          rowGroups={selectedGroups}
                          columnGroups={selectedColumnGroups}
                          matrixDateGranularity={matrixDateGranularity}
                          onMatrixDateGranularityChange={onMatrixDateGranularityChange}
                          hint="Buckets date row groups (e.g. Close Date → Mar 2026) so rows with the same day/month are not split by time"
                        />
                      </div>
                    )}
                    {renderGroupedData(
                      previewData,
                      selectedFields,
                      groupingInfo,
                      summarizedFields,
                      reportConversionBaseCode,
                      reportConversionLookup,
                      reportSortKeys,
                      formulaFields,
                      summarizedData,
                      matrixDateGranularity,
                      {},
                      reportFieldsForPicklist,
                      picklistValuesByFieldId,
                      primaryObjectName ||
                        selectedGroups[0]?.object_name ||
                        null
                    )}
                  </>
                ) : (
                  <>
                    {previewData.map((row, index) => (
                      <div key={index} className="data-row regular-row">
                        {selectedFields.map((field) => {
                          const tableName = field.object_name || 'main';
                          const aliasedFieldName = `${tableName}_${field.field_name}`;
                          const fieldData = row[aliasedFieldName] || row[field.field_name];
                          const alignRight = isRightAlignedReportField(field);
                          return (
                            <div
                              key={field.id}
                              className="data-cell"
                              style={{
                                textAlign: alignRight ? 'right' : 'left',
                                justifyContent: alignRight ? 'flex-end' : 'flex-start',
                              }}
                            >
                              {formatPreviewDataCell(fieldData, field, row)}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    <div className="data-row grand-total-row">
                      {(() => {
                        const labelIndex = findTabularGrandTotalLabelIndex(
                          selectedFields,
                          summarizedFields
                        );
                        return selectedFields.map((field, fieldIndex) => {
                          if (fieldIndex === labelIndex) {
                            return (
                              <div key={`gt-label-${field.id}`} className="data-cell grand-total-cell">
                                <span className="grand-total-label">
                                  Grand Total ({previewData.length})
                                </span>
                              </div>
                            );
                          }
                          const totalValue = getReportGrandTotalCellValue(
                            previewData,
                            field,
                            summarizedFields,
                            reportConversionBaseCode,
                            aggregationFormatter,
                            reportConversionLookup,
                            primaryObjectName
                          );
                          if (totalValue == null) {
                            return <div key={field.id} className="data-cell grand-total-empty-cell" />;
                          }
                          const alignRight = isRightAlignedReportField(field);
                          return (
                            <div
                              key={field.id}
                              className="data-cell grand-total-data-cell"
                              style={{
                                textAlign: alignRight ? 'right' : 'left',
                                justifyContent: alignRight ? 'flex-end' : 'flex-start',
                              }}
                            >
                              {totalValue}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </>
                )
              ) : (
                <div className="no-data-message report-tabular-full-width">
                  <p>No records returned in preview. Try running the report or editing report filters.</p>
                  <div className="suggestions">
                    <p>• Show <a href="#">All accounts</a>.</p>
                    <p>• Set the Created Date filter to <a href="#">All Time</a>.</p>
                    <p>• <a href="#">Edit other filters</a> in the filter panel.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="empty-preview">
          <h3>Add columns to see your report</h3>
          <p>Select fields from the left panel to build your report.</p>
        </div>
      )}
    </div>
  );
};

export default ReportPreview;
